import { createContext, PropsWithChildren, useContext, useMemo, useState } from "react";
import { useMediaQuery } from "@mui/material";

/**
 * WCAG 2.1 SC 1.4.10 Reflow requires content to be usable at 400% zoom.
 * At 400% zoom on a 1280px desktop viewport, the CSS viewport width becomes 320px.
 *
 * `(hover: hover) and (pointer: fine)` distinguishes desktop devices with a mouse/trackpad
 * from actual mobile devices (which have `hover: none` and `pointer: coarse`), so the narrow
 * viewport is only treated as "high zoom" on desktop.
 *
 * The query is evaluated once here and the result shared as `isHighZoomDesktop`, so consumers
 * (e.g. `SidebarRailToggle`) branch off the same threshold instead of redeclaring it in CSS.
 */
export const WCAG_REFLOW_BREAKPOINT = 320;
export const DESKTOP_HIGH_ZOOM_MEDIA_QUERY = `(max-width: ${WCAG_REFLOW_BREAKPOINT}px) and (hover: hover) and (pointer: fine)`;

export interface EmbeddedCncWidgetContextData {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cncWidget?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setCncWidget: (cncWidget?: any) => void;
    showReactions: boolean;
    setShowReactions: (showReactions: boolean) => void;
    /**
     * Whether the CnC sidebar rail is collapsed (hidden).
     * This is used for WCAG 2.1 SC 1.4.10 Reflow compliance - allowing users to
     * collapse the sidebar when viewing at 400% zoom on desktop browsers.
     * When collapsed, a toggle button is shown instead of the full sidebar.
     *
     * Defaults to true (collapsed) when loading the page at 400% zoom on desktop,
     * and false (expanded) otherwise.
     */
    isRailCollapsed: boolean;
    /**
     * Toggle the collapsed state of the CnC sidebar rail.
     * Used by the SidebarRailToggle component for WCAG reflow compliance.
     */
    setIsRailCollapsed: (collapsed: boolean) => void;
    /**
     * Whether the viewport is at the WCAG reflow breakpoint on a desktop device (~400% zoom) -
     * see DESKTOP_HIGH_ZOOM_MEDIA_QUERY.
     *
     * Exposed so UI that exists purely to serve reflow compliance (e.g. `SidebarRailToggle`) can
     * render off the same media query that drives the collapse state, rather than redeclaring
     * the breakpoint in CSS.
     */
    isHighZoomDesktop: boolean;
}

/**
 * Context for sharing the C&C widget object between components that are spread out across the layout regions.
 * The provider for this context should be rendered in the top level of the application (see DsLayout).
 */
const EmbeddedCncWidgetContext = createContext<EmbeddedCncWidgetContextData>({
    setCncWidget: () => {},
    showReactions: false,
    setShowReactions: () => {},
    isRailCollapsed: false,
    setIsRailCollapsed: () => {},
    isHighZoomDesktop: false,
});

export const EmbeddedCncWidgetContextProvider = ({ children }: PropsWithChildren) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [cncWidget, setCncWidget] = useState<any>();
    const [showReactions, setShowReactions] = useState(false);
    // Collapse sidebar by default when at 400% zoom on desktop (WCAG 2.1 SC 1.4.10 Reflow).
    // This ensures the content area has enough space to be usable at high zoom levels.
    // The toggle button allows users to expand it when needed.
    const isHighZoomDesktop = useMediaQuery(DESKTOP_HIGH_ZOOM_MEDIA_QUERY, { noSsr: true });
    const [isRailCollapsed, setIsRailCollapsed] = useState(isHighZoomDesktop);
    // Tracks the previously seen media query match so we can detect a change during render
    // (see https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    const [prevIsHighZoomDesktop, setPrevIsHighZoomDesktop] = useState(isHighZoomDesktop);

    // Auto-collapse sidebar when the viewport enters the WCAG reflow breakpoint (e.g., user zooms
    // to 400% after page load), and auto-expand when it exits again (e.g., user zooms out).
    // This ensures WCAG reflow compliance even when zoom level changes dynamically.
    if (isHighZoomDesktop !== prevIsHighZoomDesktop) {
        setPrevIsHighZoomDesktop(isHighZoomDesktop);
        setIsRailCollapsed(isHighZoomDesktop);
    }
    const contextData = useMemo<EmbeddedCncWidgetContextData>(
        () => ({
            cncWidget,
            setCncWidget,
            showReactions,
            setShowReactions,
            isRailCollapsed,
            setIsRailCollapsed,
            isHighZoomDesktop,
        }),
        [
            cncWidget,
            setCncWidget,
            showReactions,
            setShowReactions,
            isRailCollapsed,
            setIsRailCollapsed,
            isHighZoomDesktop,
        ]
    );
    return <EmbeddedCncWidgetContext.Provider value={contextData}>{children}</EmbeddedCncWidgetContext.Provider>;
};

export const useEmbeddedCncWidgetContext = () => useContext(EmbeddedCncWidgetContext);
